"use client"
import { useRef, useState, useEffect } from "react"
import CatalogHeader from "./catalog-header";
import CategoriesScrollbar from "./categories-scrollbar";
import SearchResults from "./search-results";
import AppSidebar from "@/components/navigation/app-sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { usePathname, useSearchParams, useRouter } from 'next/navigation'
import SubcategoriesScrollbar from "./subcategories-scrollbar";
import AuthDialog from "@/components/auth/auth-dialog";
import CitySelectionDialog from "@/components/auth/city-selection-dialog";
import AddAddressDialog from "@/components/auth/add-address-dialog";
import AppPromptDrawer from "@/components/common/app-prompt-drawer";
import { useAuthWithLocationSync } from "@/hooks/use-auth-with-location-sync";
import { useStoreLocation } from "@/context/store-location-context";
import { useMediaQuery } from "@/hooks/use-media-query";
import { CITIES_DATA } from "@/constants/cities";

export default function CatalogAndSearchWrapper({ collectionHierarchy, children }: { collectionHierarchy: any, children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchStr = searchParams.get('q') || '';
  const bypassAuth = searchParams.get('bypass_auth') === '1';
  const cityParam = searchParams.get('city');
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(64); // HACK: 64 is because the header is h-16 which is 64px according to ChatGPT

  const router = useRouter();
  const { isAuthenticated, isLoading, user } = useAuthWithLocationSync();
  const { hasStoreLocation, setStoreLocation } = useStoreLocation();
  const isMobile = !useMediaQuery("(min-width: 768px)");

  // Guest mode: user clicked "Continue as guest", or bypass_auth param is present
  const [guestMode, setGuestMode] = useState(bypassAuth);
  const [appPromptDismissed, setAppPromptDismissed] = useState(false);
  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);

  // If bypass_auth is set and there's no location yet, auto-apply city param or show city selection
  useEffect(() => {
    if (!bypassAuth || isLoading || isAuthenticated || hasStoreLocation) return;
    const matchedCity = cityParam
      ? CITIES_DATA.find((c) => c.city.toLowerCase() === cityParam.toLowerCase())
      : null;
    if (matchedCity) {
      setStoreLocation({ city: matchedCity.city, microhubId: matchedCity.defaultMicrohubId }).then(() => router.refresh());
    } else {
      setCityDialogOpen(true);
    }
  }, [bypassAuth, cityParam, isLoading, isAuthenticated, hasStoreLocation]);

  // Show forced auth dialog when not authenticated and no location is set yet
  const forcedAuthOpen = !isLoading && !isAuthenticated && !hasStoreLocation && !guestMode;

  // After login/register: show city dialog if the user's profile has no city
  useEffect(() => {
    if (!isLoading && isAuthenticated && user && !user.city && !hasStoreLocation) {
      setCityDialogOpen(true);
    }
  }, [isLoading, isAuthenticated, user?.city, user?.id, hasStoreLocation]);

  const handleGuestContinue = () => {
    setGuestMode(true);
    setCityDialogOpen(true);
  };

  const handleBackToAuth = () => {
    setCityDialogOpen(false);
    setGuestMode(false); // re-opens the forced auth dialog
  };

  const handleCitySelected = () => {
    setCityDialogOpen(false);
    router.refresh();
    // Offer address setup only for authenticated users (not guests)
    if (isAuthenticated) {
      setAddressDialogOpen(true);
    }
  };

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setHeaderHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const segments = pathname.split('/').filter(Boolean)
  let categorySlug: string = '0'
  let subcategorySlug: string = '0'

  if (segments[0] === 'categorias') {
    categorySlug = segments[1] ?? '0'
    subcategorySlug = segments[2] ?? '0'
  }

  const isCategoryPage = segments[0] === 'categorias' || !segments[0] || segments[0] === '/';
  const selectedCategory = collectionHierarchy.find((item: any) => item && item.id.toString() === categorySlug);
  const subcategories = selectedCategory && selectedCategory.subcollections ? selectedCategory.subcollections : [];
  const showScrollbars = !searchStr && isCategoryPage;

  return (
    <SidebarProvider defaultOpen={true} className="flex-col" style={{ "--sidebar-width": "15rem" } as React.CSSProperties}>
      <header ref={headerRef} className="sticky top-0 z-30 bg-background/100 backdrop-blur">
        <CatalogHeader searchStr={searchStr} />
      </header>
      <div className="flex flex-1 min-h-0">
        <AppSidebar headerHeight={headerHeight} collectionHierarchy={collectionHierarchy} categorySlug={categorySlug} subcategorySlug={subcategorySlug} />
        <main className="flex flex-col flex-1 min-w-0 pb-16 md:pb-0">
          {showScrollbars && (
            <div className="md:sticky md:z-50 md:bg-background/100 md:backdrop-blur" style={{ top: `${headerHeight}px` }}>
              <CategoriesScrollbar categorySlug={categorySlug} collectionHierarchy={collectionHierarchy} />
              {subcategories.length > 0 && <SubcategoriesScrollbar categorySlug={categorySlug} subcategorySlug={subcategorySlug} subcategories={subcategories} />}
            </div>
          )}
          {!!searchStr && <SearchResults searchStr={searchStr} />}
          {!searchStr && <>{children}</>}
        </main>
      </div>

      {/* On mobile: show app prompt first; on desktop (or after dismissal): show auth dialog */}
      <AppPromptDrawer
        open={forcedAuthOpen && isMobile && !appPromptDismissed}
        onContinueInBrowser={() => setAppPromptDismissed(true)}
      />
      <AuthDialog
        open={forcedAuthOpen && (!isMobile || appPromptDismissed)}
        onOpenChange={() => {}}
        dismissable={false}
        showGuestOption
        onGuestContinue={handleGuestContinue}
        isInitiallyOnRegistrationView={false}
      />

      {/* City selection — shown after guest continue or after login with no city */}
      <CitySelectionDialog
        open={cityDialogOpen}
        onCitySelected={handleCitySelected}
        onBack={!isAuthenticated ? handleBackToAuth : undefined}
      />

      {/* Optional: add delivery address after city selection (authenticated users only) */}
      <AddAddressDialog
        open={addressDialogOpen}
        onSuccess={() => { setAddressDialogOpen(false); router.refresh(); }}
        onSkip={() => setAddressDialogOpen(false)}
      />
    </SidebarProvider>
  )
}
